import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Spinner } from "react-bootstrap";
import { LuSearch, LuPhone, LuCopy, LuEye, LuUser, LuUsers, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { getRestaurantClients } from "../actions/userActions";
import ShowCustomerInfo from "../components/ShowCustomerInfo";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  DirectoryPage, DirectoryHeader, DirectoryTitle, Toolbar, SearchField, ResultCount,
  DirectoryList, DirectoryRow, Identity, Avatar, CustomerButton, Contact, Actions,
  Metric, IconButton, DirectoryButton, EmptyState, Pagination,
} from "../components/directory/directoryStyles";

function RestaurantClients() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isShowCustomerInfoModalActive, setIsShowCustomerInfoModalActive] = useState(false);
  const [completePhone, setCompletePhone] = useState("");
  const { restaurantClients, isLoadingGetRestaurantClients, errorGetRestaurantClients } =
    useSelector(state => state.restaurant_clients);
  const restaurantId = useSelector(state => state.restaurantDetails.restaurants?.id);
  const totalPages = Math.ceil((restaurantClients?.total_results || 0) / 10);
  const results = restaurantClients?.results || [];

  useEffect(() => {
    setCurrentPage(1);
  }, [restaurantId]);
  useEffect(() => {
    if (restaurantId) dispatch(getRestaurantClients(restaurantId, currentPage, debouncedSearchTerm));
  }, [dispatch, restaurantId, currentPage, debouncedSearchTerm]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const copyToClipboard = async phone => {
    try {
      await navigator.clipboard.writeText(phone);
      toast.success(t("ui.i18nMessages.numberCopied"), { autoClose: 1000 });
    } catch {
      toast.error(t("directoryDesign.copyFailed"));
    }
  };
  const showCustomerInfo = phone => {
    setCompletePhone(phone);
    setIsShowCustomerInfoModalActive(true);
  };

  return (
    <DirectoryPage>
      <ToastContainer />
      <DirectoryHeader>
        <DirectoryTitle as="h1">{t("ui.screens.restaurantClients.clientes")}</DirectoryTitle>
      </DirectoryHeader>
      <Toolbar>
        <SearchField>
          <LuSearch aria-hidden="true" />
          <input type="search" aria-label={t("ui.screens.restaurantClients.procurarCliente")}
            placeholder={t("ui.screens.restaurantClients.procurarCliente")} value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)} />
        </SearchField>
        <ResultCount aria-live="polite">{t("directoryDesign.results", { count: restaurantClients?.total_results || 0 })}</ResultCount>
      </Toolbar>
      {isLoadingGetRestaurantClients ? (
        <EmptyState role="status"><Spinner animation="border" size="sm" />
          <p>{t("ui.screens.restaurantClients.carregandoClientes")}</p>
        </EmptyState>
      ) : errorGetRestaurantClients ? (
        <EmptyState role="alert">{t("directoryDesign.loadFailed")}</EmptyState>
      ) : results.length ? (
        <DirectoryList aria-label={t("ui.screens.restaurantClients.clientes")}>
          {results.map(client => {
            const phone = String(client.client.phone_number || "");
            return (
              <DirectoryRow key={client.id}>
                <Identity>
                  <Avatar><LuUser aria-hidden="true" /></Avatar>
                  <CustomerButton onClick={() => showCustomerInfo(phone)}
                    aria-label={t("directoryDesign.viewCustomer", { name: client.client.name })}>
                    {client.client.name}<LuEye aria-hidden="true" />
                  </CustomerButton>
                </Identity>
                <Contact>
                  <LuPhone aria-hidden="true" />
                  <span>{phone ? `+${phone.replace(/^\+/, "")}` : "—"}</span>
                  {phone && <IconButton onClick={() => copyToClipboard(phone)}
                    aria-label={t("directoryDesign.copyPhone")} title={t("directoryDesign.copyPhone")}>
                    <LuCopy aria-hidden="true" />
                  </IconButton>}
                </Contact>
                <Actions>
                  <Metric>{t("directoryDesign.reservations")}<strong>{client.number_of_reservations}</strong></Metric>
                  <Metric $warning={client.did_not_show_up_times > 0}>
                    {t("directoryDesign.noShows")}<strong>{client.did_not_show_up_times}</strong>
                  </Metric>
                </Actions>
              </DirectoryRow>
            );
          })}
        </DirectoryList>
      ) : (
        <EmptyState><LuUsers aria-hidden="true" /><p>{t("ui.screens.restaurantClients.nenhumClienteEncontrado")}</p></EmptyState>
      )}
      {totalPages > 1 && (
        <Pagination aria-label={t("directoryDesign.pagination")}>
          <DirectoryButton disabled={currentPage <= 1 || isLoadingGetRestaurantClients} onClick={() => setCurrentPage(page => page - 1)}
            aria-label={t("ui.screens.restaurantClients.anterior")}><LuChevronLeft aria-hidden="true" /></DirectoryButton>
          <span>{t("directoryDesign.page", { current: currentPage, total: totalPages })}</span>
          <DirectoryButton disabled={currentPage >= totalPages || isLoadingGetRestaurantClients} onClick={() => setCurrentPage(page => page + 1)}
            aria-label={t("ui.screens.restaurantClients.proxima")}><LuChevronRight aria-hidden="true" /></DirectoryButton>
        </Pagination>
      )}
      <ShowCustomerInfo phone_number={completePhone} restaurant_id={restaurantId}
        isModalActive={isShowCustomerInfoModalActive} setIsModalActive={setIsShowCustomerInfoModalActive} />
    </DirectoryPage>
  );
}
export default RestaurantClients;
